import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiEdit2, FiClock, FiUser, FiCalendar, FiTag, FiBriefcase, FiImage, FiX } from 'react-icons/fi';
import Toast from '../../components/Toast';
import api from '../../services/api';

const ManagerTaskDetailPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const taskResponse = await api.get(`/manager/tasks/${taskId}`);
      setTask(taskResponse.data);
      
      try {
        const commentsResponse = await api.get(`/tasks/${taskId}/comments`);
        setComments(commentsResponse.data || []);
      } catch (commentErr) {
        console.error('Error fetching comments:', commentErr);
        setComments([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching task details:', err);
      setError('Failed to load task details: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && selectedImages.length === 0) return;
    
    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append('content', newComment);
      
      selectedImages.forEach((image, index) => {
        formData.append('images', image);
      });
      
      const response = await api.post(`/tasks/${taskId}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNewComment('');
      setSelectedImages([]);
      setComments(prevComments => [response.data, ...prevComments]);
      setSubmitting(false);
      
      setToast({
        message: 'Comment added successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error submitting comment:', err);
      setToast({
        message: 'Failed to submit comment: ' + (err.response?.data?.message || err.message),
        type: 'error'
      });
      setSubmitting(false);
    }
  };
  
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validImages = files.filter(file => 
      ['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)
    );
    setSelectedImages(prev => [...prev, ...validImages]);
  };
  
  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const statusOptions = {
    todo: 'To Do',
    in_progress: 'In Progress',
    testing: 'Testing',
    client_review: 'Client Review',
    completed: 'Completed'
  };
  
  const statusClasses = {
    todo: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-blue-100 text-blue-800',
    testing: 'bg-purple-100 text-purple-800',
    client_review: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800'
  };
  
  const handleStatusChange = async (newStatus) => {
    if (newStatus === task.status) return;
    
    try {
      setUpdatingStatus(true);
      const response = await api.put(`/manager/tasks/${taskId}`, { 
        status: newStatus
      });
      
      setTask(prev => ({ ...prev, status: newStatus }));
      
      setToast({
        message: `Status updated to ${statusOptions[newStatus] || newStatus}`,
        type: 'success'
      });
      
      const statusComment = `Status changed from ${statusOptions[task.status] || task.status} to ${statusOptions[newStatus] || newStatus}`;
      const commentResponse = await api.post(`/tasks/${taskId}/comments`, { content: statusComment });
      setComments(prevComments => [commentResponse.data, ...prevComments]);
      
    } catch (err) {
      console.error('Error updating task status:', err);
      setToast({
        message: 'Failed to update status: ' + (err.response?.data?.message || err.message),
        type: 'error'
      });
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const getStatusBadge = (status) => {
    return (
      <span className={`px-3 py-1 text-sm rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusOptions[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-3 py-1 text-sm rounded-full ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading task details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchTaskDetails} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">Task not found</div>
        <button 
          onClick={() => navigate(-1)} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FiArrowLeft className="mr-2" /> Back to Tasks
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl font-semibold text-gray-900">{task.title}</h1>
            <div className="flex space-x-2">
              {getPriorityBadge(task.priority)}
            </div>
          </div>
          
          {/* Status Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status:</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(statusOptions).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleStatusChange(value)}
                  disabled={updatingStatus}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${task.status === value 
                    ? `${statusClasses[value]} border-2 border-blue-500` 
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="mb-4">
                <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: task.description || '' }} />
              </div>

              {task.Project && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiTag className="mr-2" />
                  <span>Project: {task.Project.name}</span>
                </div>
              )}

              {task.Client && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <FiBriefcase className="mr-2" />
                  <span>Client: {task.Client.name}</span>
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Task Details</h3>
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiCalendar className="mr-2" />
                <span>Due Date: {formatDate(task.due_date)}</span>
              </div>
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiClock className="mr-2" />
                <span>Created: {formatDate(task.created_at)}</span>
              </div>
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <FiClock className="mr-2" />
                <span>Updated: {formatDate(task.updated_at)}</span>
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Assigned To:</h4>
                <div className="flex flex-wrap gap-2">
                  {task.Users && task.Users.length > 0 ? (
                    task.Users.map(user => (
                      <div key={user.id} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center">
                        <FiUser className="mr-1" /> {user.name}
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-500">Unassigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h2 className="text-lg font-medium text-gray-900">Comments</h2>
        </div>
        
        <div className="p-6">
          {/* Comment List */}
          <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
            {comments && comments.length > 0 ? (
              comments.map(comment => (
                <div key={comment.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-medium">
                        {comment.User?.name?.charAt(0) || 'U'}
                      </div>
                      <span className="ml-2 font-medium">{comment.User?.name || 'Unknown User'}</span>
                    </div>
                    <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                  {comment.images && (
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {(() => {
                        try {
                          return JSON.parse(comment.images).map((imageUrl, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${imageUrl}`}
                              alt={`Comment image ${imgIndex + 1}`}
                              className="w-full h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => window.open(`${process.env.REACT_APP_SERVER_URL || 'https://taskflowapi.webseller.in'}${imageUrl}`, '_blank')}
                            />
                          ));
                        } catch (e) {
                          console.error('Error parsing images:', e);
                          return null;
                        }
                      })()
                      }
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No comments yet</p>
            )}
          </div>
          
          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="mt-4">
            <div className="mb-4">
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
                Add a comment
              </label>
              <textarea
                id="comment"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Type your comment here..."
              ></textarea>
            </div>
            
            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attach Images
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  id="images"
                  multiple
                  accept=".png,.jpg,.jpeg"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <label
                  htmlFor="images"
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer flex items-center"
                >
                  <FiImage className="mr-2" /> Select Images
                </label>
                <span className="text-xs text-gray-500">PNG, JPG, JPEG only</span>
              </div>
              
              {/* Selected Images Preview */}
              {selectedImages.length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <FiX size={12} />
                      </button>
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {image.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                disabled={submitting || (!newComment.trim() && selectedImages.length === 0)}
              >
                <FiSend className="mr-2" /> {submitting ? 'Sending...' : 'Send Comment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManagerTaskDetailPage;